// src/components/shared/BookletViewer.jsx
import { confirmDialog } from '../../utils/notify';
// Full-screen single-page answer sheet viewer with realistic 3D page-flip animation and drawing annotations.

import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';

// ─── CSS Styles for premium 3D page-flip and spiral book styling ────────────────
const FLIP_CSS = `
  .bv-stage {
    perspective: 2000px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
  }
  
  .bv-book-container {
    position: relative;
    transform-style: preserve-3d;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border-radius: 4px;
    box-shadow: 
      1px 1px 0 #cbd5e1,
      2px 2px 0 #cbd5e1,
      3px 3px 0 #94a3b8,
      4px 4px 15px rgba(0,0,0,0.3);
  }

  .bv-page-stack {
    position: absolute;
    inset: 0;
    background: #ffffff;
    transform-style: preserve-3d;
    backface-visibility: hidden;
    border-radius: 4px;
  }

  /* Spiral binding styling along the left edge */
  .bv-spiral-binding {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 18px;
    background: linear-gradient(90deg, #1e293b 0%, #0f172a 100%);
    border-right: 1px solid rgba(255,255,255,0.1);
    box-shadow: 2px 0 5px rgba(0,0,0,0.2);
    z-index: 50;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    padding: 20px 0;
    pointer-events: none;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  .bv-spiral-ring {
    width: 26px;
    height: 6px;
    background: linear-gradient(180deg, #94a3b8 0%, #e2e8f0 50%, #475569 100%);
    border-radius: 3px;
    box-shadow: 1px 1px 3px rgba(0,0,0,0.35);
    transform: translateX(-4px);
  }

  /* Crease shadow on page margins */
  .bv-spine-crease-left {
    position: absolute;
    left: 18px;
    top: 0;
    bottom: 0;
    width: 16px;
    background: linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0) 100%);
    z-index: 40;
    pointer-events: none;
  }

  /* Leaf container with preserve-3d */
  .bv-leaf-3d {
    position: absolute;
    inset: 0;
    transform-style: preserve-3d;
    transform-origin: left center;
    z-index: 15;
    pointer-events: none;
  }

  .bv-leaf-face {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    background: #fff;
    overflow: hidden;
    border-radius: 4px;
  }

  .bv-leaf-front {
    transform: rotateY(0deg);
    z-index: 2;
  }

  .bv-leaf-back {
    transform: rotateY(180deg) scaleX(-1); /* Flips to back and mirrors horizontally to keep PDF text readable */
    z-index: 1;
  }

  .bv-thumb:hover {
    transform: scale(1.18);
    background: #319ab9 !important;
    color: #fff !important;
  }

  .bv-dragging {
    cursor: grabbing !important;
  }
`;

function injectFlipStyles() {
  if (document.getElementById('bv-flip-css')) return;
  const tag = document.createElement('style');
  tag.id = 'bv-flip-css';
  tag.textContent = FLIP_CSS;
  document.head.appendChild(tag);
}

// ─── Load PDF.js CDN ──────────────────────────────────────────────────────────
function usePdfJs() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    injectFlipStyles();
    if (window.pdfjsLib) { setReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      setReady(true);
    };
    document.body.appendChild(s);
  }, []);
  return ready;
}

const DIR_NONE = 0;
const DIR_NEXT = 1;
const DIR_PREV = 2;

function copyCanvas(src, dest) {
  if (!src || !dest) return;
  dest.width = src.width;
  dest.height = src.height;
  const ctx = dest.getContext('2d');
  ctx.drawImage(src, 0, 0);
}

export default function BookletViewer({
  pdfUrl,
  studentName = '',
  studentId = '',
  subject = '',
  onClose,
  fallbackSheets = [],
  initialAnnotations = {},
  onAnnotationsChange,
  readOnly = true,
}) {
  const pdfJsReady = usePdfJs();

  // ── State ──────────────────────────────────────────────────────────────────
  const [pdfDoc, setPdfDoc] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [zoom, setZoom] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [inputPage, setInputPage] = useState('1');
  const [pageSize, setPageSize] = useState({ width: 595, height: 842 });
  const [scale, setScale] = useState(1);

  // ── 3D Animation & Dragging state ──────────────────────────────────────────
  const [isAnimating, setIsAnimating] = useState(false);
  const [animDir, setAnimDir] = useState(DIR_NONE);
  const [targetPage, setTargetPage] = useState(1);
  const [dragAngle, setDragAngle] = useState(null);

  // ── Annotation / Drawing state ─────────────────────────────────────────────
  const [history, setHistory] = useState(initialAnnotations || {});
  const [redoHistory, setRedoHistory] = useState({});
  const [isDrawing, setIsDrawing] = useState(false);
  const [activeStroke, setActiveStroke] = useState([]);
  const brushSize = 2.5;

  // ── Refs ───────────────────────────────────────────────────────────────────
  const canvasUnderneathRef = useRef(null);
  const canvasFlipFrontRef = useRef(null);
  const canvasFlipBackRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const canvasCache = useRef({});
  const renderingTasks = useRef({});
  const dragStartRef = useRef({ active: false, x: 0, y: 0 });

  // Sync annotations when initialAnnotations changes
  useEffect(() => {
    if (initialAnnotations) {
      setHistory(initialAnnotations);
      canvasCache.current = {}; // clear cache to force redraw with new annotations
    }
  }, [initialAnnotations]);

  // Cleanup Cache on PDF URL change
  useEffect(() => {
    canvasCache.current = {};
    renderingTasks.current = {};
  }, [pdfUrl]);

  // Clear page-wise cache
  const clearPageCache = (p) => {
    Object.keys(canvasCache.current).forEach(key => {
      if (key.startsWith(`${p}_`)) {
        delete canvasCache.current[key];
      }
    });
  };

  // ── Load PDF ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfUrl || !pdfJsReady) return;
    setPdfLoading(true);
    setPdfError('');
    setPdfDoc(null);
    setTotalPages(0);
    setPageNum(1);
    setInputPage('1');
    setIsAnimating(false);
    setAnimDir(DIR_NONE);

    let loadedDoc = null;
    let isCancelled = false;

    const token = localStorage.getItem('access_token');
    const docParams = { url: pdfUrl, withCredentials: false };
    if (token) {
      docParams.httpHeaders = {
        'Authorization': `Bearer ${token}`
      };
    }
    const task = window.pdfjsLib.getDocument(docParams);
    task.promise
      .then(doc => {
        if (isCancelled) {
          try { doc.destroy(); } catch (_) {}
          return;
        }
        loadedDoc = doc;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
      })
      .catch(err => {
        if (isCancelled) return;
        console.error('[BookletViewer] PDF load error:', err);
        setPdfError(`Could not load PDF: ${err.message}`);
      })
      .finally(() => {
        if (!isCancelled) setPdfLoading(false);
      });

    return () => {
      isCancelled = true;
      if (loadedDoc) {
        try { loadedDoc.destroy(); } catch (_) {}
      } else {
        try { task.destroy(); } catch (_) {}
      }
    };
  }, [pdfUrl, pdfJsReady]);

  // ── Render Page with Annotations Burned In ─────────────────────────────────
  const getOrRenderPage = useCallback(async (p, z) => {
    if (!pdfDoc || p < 1 || p > pdfDoc.numPages) return null;
    const cacheKey = `${p}_${z}`;
    if (canvasCache.current[cacheKey]) {
      return canvasCache.current[cacheKey];
    }
    if (renderingTasks.current[cacheKey]) {
      return renderingTasks.current[cacheKey];
    }

    const promise = (async () => {
      try {
        const page = await pdfDoc.getPage(p);
        const viewport = page.getViewport({ scale: z });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Render PDF page
        const renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;

        // Burn drawing annotations onto page
        const pageStrokes = history[p] || [];
        if (pageStrokes.length > 0) {
          ctx.strokeStyle = '#ef4444';
          ctx.lineWidth = brushSize * z; // Proportional width based on zoom scale
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          pageStrokes.forEach(stroke => {
            if (stroke.length < 2) return;
            ctx.beginPath();
            ctx.moveTo(stroke[0].x * z, stroke[0].y * z);
            for (let i = 1; i < stroke.length; i++) {
              ctx.lineTo(stroke[i].x * z, stroke[i].y * z);
            }
            ctx.stroke();
          });
        }

        canvasCache.current[cacheKey] = canvas;
        return canvas;
      } catch (err) {
        console.error(`[BookletViewer] Render error for page ${p}:`, err);
        return null;
      } finally {
        delete renderingTasks.current[cacheKey];
      }
    })();

    renderingTasks.current[cacheKey] = promise;
    return promise;
  }, [pdfDoc, history]);

  // ── Pre-render Adjacent Pages in Background ────────────────────────────────
  useEffect(() => {
    if (!pdfDoc) return;
    const pagesToPreRender = [pageNum - 1, pageNum, pageNum + 1, pageNum + 2].filter(
      p => p >= 1 && p <= totalPages
    );
    pagesToPreRender.forEach(p => {
      getOrRenderPage(p, zoom).catch(() => {});
    });

    // Update container page sizes
    pdfDoc.getPage(pageNum).then(page => {
      const vp = page.getViewport({ scale: zoom });
      setPageSize({ width: vp.width, height: vp.height });
    });
  }, [pdfDoc, pageNum, zoom, totalPages, getOrRenderPage]);

  // ── Auto-Scale Stage Responsiveness ────────────────────────────────────────
  useEffect(() => {
    if (!pageSize.width) return;
    const handleResize = () => {
      const stage = containerRef.current;
      if (!stage) return;
      const paddingX = 40;
      const paddingY = 140;
      const availWidth = stage.clientWidth - paddingX;
      const availHeight = stage.clientHeight - paddingY;

      const scaleX = availWidth / pageSize.width;
      const scaleY = availHeight / pageSize.height;

      setScale(Math.min(1.0, scaleX, scaleY));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pageSize.width, pageSize.height, zoom]);

  const show3D = isAnimating || dragAngle !== null;
  const currentAngle = dragAngle !== null ? dragAngle : (isAnimating ? -90 : 0);

  // ── Render / Copy Canvases to DOM with Next Page timing threshold ───────────
  useEffect(() => {
    if (!pdfDoc) return;

    let cancelled = false;

    const updateCanvasRef = async (p, ref) => {
      if (!ref.current) return;
      if (p < 1 || p > totalPages) {
        const ctx = ref.current.getContext('2d');
        ctx.clearRect(0, 0, ref.current.width, ref.current.height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, ref.current.width, ref.current.height);
        return;
      }
      const cached = await getOrRenderPage(p, zoom);
      if (cancelled || !ref.current) return;
      if (cached) {
        copyCanvas(cached, ref.current);
      }
    };

    if (!show3D) {
      // Idle: render current active page
      updateCanvasRef(pageNum, canvasUnderneathRef);
    } else {
      // 3D Flip in progress
      // Implement Next Page timing: Reveal target page only after the page crosses threshold (-110deg)
      const isNext = animDir === DIR_NEXT;
      
      const pageUnder = isNext 
        ? (currentAngle <= -110 ? targetPage : -1) // -1 renders blank white page
        : (animDir === DIR_PREV 
           ? (currentAngle >= -70 ? targetPage : pageNum) 
           : pageNum);

      const pageFront = isNext ? pageNum : -1;
      const pageBack = isNext ? -1 : targetPage;

      updateCanvasRef(pageUnder, canvasUnderneathRef);
      updateCanvasRef(pageFront, canvasFlipFrontRef);
      updateCanvasRef(pageBack, canvasFlipBackRef);
    }

    return () => { cancelled = true; };
  }, [pdfDoc, pageNum, targetPage, animDir, zoom, show3D, currentAngle, getOrRenderPage, totalPages]);

  // ── Trigger 3D Page Turn Animation ──────────────────────────────────────────
  const goToPage = useCallback(async (next, dir = DIR_NONE) => {
    if (!pdfDoc || isAnimating || dragAngle !== null) return;
    const clamped = Math.max(1, Math.min(totalPages, next));
    if (clamped === pageNum) return;

    const resolvedDir = dir !== DIR_NONE ? dir : clamped > pageNum ? DIR_NEXT : DIR_PREV;

    setTargetPage(clamped);
    setAnimDir(resolvedDir);
    setIsAnimating(true);

    const startAngle = resolvedDir === DIR_NEXT ? 0 : -180;
    const endAngle = resolvedDir === DIR_NEXT ? -180 : 0;

    let startTime = null;
    const duration = 520; // Natural page flip speed

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min(1, (timestamp - startTime) / duration);
      const ease = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2; // easeInOutQuad
      const currentVal = startAngle + (endAngle - startAngle) * ease;

      setDragAngle(currentVal);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setPageNum(clamped);
        setInputPage(String(clamped));
        setIsAnimating(false);
        setAnimDir(DIR_NONE);
        setDragAngle(null);
      }
    };

    requestAnimationFrame(step);
  }, [pdfDoc, pageNum, totalPages, isAnimating, dragAngle]);

  const nextPage = useCallback(() => goToPage(pageNum + 1, DIR_NEXT), [goToPage, pageNum]);
  const prevPage = useCallback(() => goToPage(pageNum - 1, DIR_PREV), [goToPage, pageNum]);

  // ── Keyboard Controls ──────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      // Don't flip page if cursor is typing inside input page box
      if (document.activeElement?.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') nextPage();
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') prevPage();
      else if (e.key === 'Escape') onClose?.();
      else if (e.key === '+' || e.key === '=') setZoom(z => Math.min(3, z + 0.15));
      else if (e.key === '-') setZoom(z => Math.max(0.5, z - 0.15));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [nextPage, prevPage, onClose]);

  // ── Pointer Drag/Swipe Mechanics ───────────────────────────────────────────
  const onPointerDown = (e) => {
    if (!pdfDoc || isAnimating || dragAngle !== null || !readOnly) return; // Disable drag flip in drawing mode
    dragStartRef.current = { active: true, x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragStartRef.current.active) return;
    const dx = e.clientX - dragStartRef.current.x;
    const width = pageSize.width;
    const pct = dx / width;

    if (dragAngle === null) {
      if (dx < 0 && pageNum < totalPages) {
        setTargetPage(pageNum + 1);
        setAnimDir(DIR_NEXT);
        setDragAngle(0);
      } else if (dx > 0 && pageNum > 1) {
        setTargetPage(pageNum - 1);
        setAnimDir(DIR_PREV);
        setDragAngle(-180);
      }
      return;
    }

    if (animDir === DIR_NEXT) {
      const angle = Math.max(-180, Math.min(0, pct * 180));
      setDragAngle(angle);
    } else if (animDir === DIR_PREV) {
      const angle = Math.max(-180, Math.min(0, -180 + pct * 180));
      setDragAngle(angle);
    }
  };

  const onPointerUp = (e) => {
    if (!dragStartRef.current.active) return;
    dragStartRef.current.active = false;
    e.currentTarget.releasePointerCapture(e.pointerId);

    if (dragAngle === null) return;

    const currentVal = dragAngle;
    const resolveFlip = (currentVal < -75 && animDir === DIR_NEXT) || (currentVal > -105 && animDir === DIR_PREV);

    const startAngle = currentVal;
    const endAngle = resolveFlip ? (animDir === DIR_NEXT ? -180 : 0) : (animDir === DIR_NEXT ? 0 : -180);

    let startTime = null;
    const duration = 220;

    const snapStep = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min(1, (timestamp - startTime) / duration);
      const ease = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const angle = startAngle + (endAngle - startAngle) * ease;

      setDragAngle(angle);

      if (progress < 1) {
        requestAnimationFrame(snapStep);
      } else {
        if (resolveFlip) {
          setPageNum(targetPage);
          setInputPage(String(targetPage));
        }
        setIsAnimating(false);
        setAnimDir(DIR_NONE);
        setDragAngle(null);
      }
    };

    requestAnimationFrame(snapStep);
  };

  // ── Drawing Canvas Coordinators ────────────────────────────────────────────
  const getCoordinates = (e) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return { pdfX: 0, pdfY: 0, pixelX: 0, pixelY: 0 };
    const rect = canvas.getBoundingClientRect();
    
    // Support touch / mouse events
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    // Calculate pixel coordinates relative to the canvas's physical scale size on screen
    const pixelX = (clientX - rect.left) / scale;
    const pixelY = (clientY - rect.top) / scale;

    return {
      pixelX,
      pixelY,
      pdfX: pixelX / zoom, // Normalized coordinate for scale: 1.0 PDF
      pdfY: pixelY / zoom,
    };
  };

  // ── Drawing Event Handlers ─────────────────────────────────────────────────
  const startDrawing = (e) => {
    if (readOnly) return;
    const { pdfX, pdfY, pixelX, pixelY } = getCoordinates(e);
    setIsDrawing(true);
    setActiveStroke([{ x: pdfX, y: pdfY }]);

    // Clear redo history for this page
    setRedoHistory(prev => ({ ...prev, [pageNum]: [] }));

    // Render local dot instantly
    const canvas = drawCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.arc(pixelX, pixelY, brushSize / 2, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { pdfX, pdfY, pixelX, pixelY } = getCoordinates(e);
    
    // Add point to stroke
    setActiveStroke(prev => [...prev, { x: pdfX, y: pdfY }]);

    const canvas = drawCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const lastPoint = activeStroke[activeStroke.length - 1];
      if (lastPoint) {
        ctx.beginPath();
        ctx.moveTo(lastPoint.x * zoom, lastPoint.y * zoom);
        ctx.lineTo(pixelX, pixelY);
        ctx.stroke();
      }
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    if (activeStroke.length > 1) {
      const updatedHistory = {
        ...history,
        [pageNum]: [...(history[pageNum] || []), activeStroke]
      };
      setHistory(updatedHistory);
      
      // Force page cache redraw with new stroke
      clearPageCache(pageNum);

      if (onAnnotationsChange) {
        onAnnotationsChange(updatedHistory);
      }
    }

    // Clear the active scratch drawing canvas
    const canvas = drawCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setActiveStroke([]);
  };

  // ── Undo / Redo / Clear Tools ──────────────────────────────────────────────
  const handleUndo = () => {
    const pageStrokes = history[pageNum] || [];
    if (pageStrokes.length === 0) return;

    const last = pageStrokes[pageStrokes.length - 1];
    const newPageStrokes = pageStrokes.slice(0, -1);
    const updatedHistory = { ...history, [pageNum]: newPageStrokes };

    setHistory(updatedHistory);
    setRedoHistory(prev => ({
      ...prev,
      [pageNum]: [...(prev[pageNum] || []), last]
    }));

    clearPageCache(pageNum);

    if (onAnnotationsChange) {
      onAnnotationsChange(updatedHistory);
    }
  };

  const handleRedo = () => {
    const pageRedos = redoHistory[pageNum] || [];
    if (pageRedos.length === 0) return;

    const last = pageRedos[pageRedos.length - 1];
    const newPageRedos = pageRedos.slice(0, -1);
    const updatedHistory = {
      ...history,
      [pageNum]: [...(history[pageNum] || []), last]
    };

    setHistory(updatedHistory);
    setRedoHistory(prev => ({
      ...prev,
      [pageNum]: newPageRedos
    }));

    clearPageCache(pageNum);

    if (onAnnotationsChange) {
      onAnnotationsChange(updatedHistory);
    }
  };

  const handleClearPage = async () => {
    const pageStrokes = history[pageNum] || [];
    if (pageStrokes.length === 0) return;

    if (!(await confirmDialog('Clear all drawings on this page?'))) return;

    const updatedHistory = { ...history, [pageNum]: [] };
    setHistory(updatedHistory);
    setRedoHistory(prev => ({
      ...prev,
      [pageNum]: [...(prev[pageNum] || []), ...pageStrokes]
    }));

    clearPageCache(pageNum);

    if (onAnnotationsChange) {
      onAnnotationsChange(updatedHistory);
    }
  };

  // ── Fullscreen Toggle ──────────────────────────────────────────────────────
  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };
  
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  const progressPct = totalPages > 0 ? (pageNum / totalPages) * 100 : 0;
  const zoomIn = () => setZoom(z => Math.min(3, parseFloat((z + 0.15).toFixed(2))));
  const zoomOut = () => setZoom(z => Math.max(0.5, parseFloat((z - 0.15).toFixed(2))));

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(4, 8, 18, 0.95)',
        backdropFilter: 'blur(10px)',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'DM Sans', 'Inter', 'Segoe UI', sans-serif",
        userSelect: 'none',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px',
        background: 'linear-gradient(180deg, #0c1428 0%, #0f172a 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        flexShrink: 0, gap: 12, flexWrap: 'wrap',
      }}>
        {/* Left: Metadata */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            background: 'linear-gradient(135deg, #2563eb 0%, #319ab9 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, boxShadow: '0 0 0 1px rgba(49,154,185,0.4)',
          }}>📄</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 13, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
              {studentName || 'Answer Sheet Viewer'}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
              {[studentId, subject].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>

        {/* Center tools: Drawings, Undo, Redo */}
        {!readOnly && pdfDoc && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#1e293b/40', padding: '3px 8px', borderRadius: 10, border: '1px solid #334155' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, paddingRight: 4 }}>✍️ RED PEN</span>
            <button
              onClick={handleUndo}
              disabled={!(history[pageNum] && history[pageNum].length > 0)}
              style={toolBtnStyle}
              title="Undo Stroke"
            >
              ↩
            </button>
            <button
              onClick={handleRedo}
              disabled={!(redoHistory[pageNum] && redoHistory[pageNum].length > 0)}
              style={toolBtnStyle}
              title="Redo Stroke"
            >
              ↪
            </button>
            <button
              onClick={handleClearPage}
              disabled={!(history[pageNum] && history[pageNum].length > 0)}
              style={{ ...toolBtnStyle, color: '#f87171' }}
              title="Clear Page Drawings"
            >
              🗑
            </button>
          </div>
        )}

        {/* Center: page nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button
            onClick={prevPage}
            disabled={pageNum <= 1 || !pdfDoc || isAnimating || dragAngle !== null}
            style={navBtnStyle(pageNum <= 1 || !pdfDoc || isAnimating || dragAngle !== null)}
            title="Previous Page (←)"
          >◀ Prev</button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <input
              type="number"
              min={1} max={totalPages || 1}
              value={inputPage}
              onChange={e => setInputPage(e.target.value)}
              onBlur={() => {
                const n = parseInt(inputPage, 10);
                if (!isNaN(n)) goToPage(n);
                else setInputPage(String(pageNum));
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const n = parseInt(inputPage, 10);
                  if (!isNaN(n)) goToPage(n);
                }
              }}
              style={{
                width: 44, textAlign: 'center',
                background: '#1e293b', border: '1px solid #334155',
                borderRadius: 6, color: '#f1f5f9', fontSize: 12,
                padding: '4px 0', outline: 'none',
              }}
            />
            <span style={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
              / {totalPages || '—'}
            </span>
          </div>

          <button
            onClick={nextPage}
            disabled={pageNum >= totalPages || !pdfDoc || isAnimating || dragAngle !== null}
            style={navBtnStyle(pageNum >= totalPages || !pdfDoc || isAnimating || dragAngle !== null)}
            title="Next Page (→)"
          >Next ▶</button>
        </div>

        {/* Right: tools */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button onClick={zoomOut} style={toolBtnStyle} title="Zoom Out (−)">−</button>
          <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 36, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button onClick={zoomIn} style={toolBtnStyle} title="Zoom In (+)">+</button>
          <button onClick={() => setZoom(1)} style={{ ...toolBtnStyle, fontSize: 10, width: 36 }} title="Reset Zoom">1:1</button>

          <div style={{ width: 1, height: 18, background: '#1e293b', margin: '0 2px' }} />

          <button onClick={toggleFullscreen} style={toolBtnStyle} title="Toggle Fullscreen">
            {isFullscreen ? '⊡' : '⛶'}
          </button>
          <button
            onClick={onClose}
            style={{
              ...toolBtnStyle, width: 30, height: 30,
              background: 'rgba(239,68,68,0.14)',
              color: '#f87171', border: '1px solid rgba(239,68,68,0.28)',
            }}
            title="Close (Esc)"
          >✕</button>
        </div>
      </div>

      {/* ── Progress bar ────────────────────────────────────────────────── */}
      {totalPages > 0 && (
        <div style={{ height: 3, background: '#0f172a', flexShrink: 0 }}>
          <div style={{
            height: '100%', width: `${progressPct}%`,
            background: 'linear-gradient(90deg, #2563eb, #319ab9)',
            transition: 'width 0.35s ease',
          }} />
        </div>
      )}

      {/* ── Single Booklet Stage ────────────────────────────────────────── */}
      <div
        style={{
          flex: 1, overflow: 'hidden', minHeight: 0,
          display: 'flex', alignItems: 'center', justifycontent: 'center',
          padding: '20px 16px 20px',
          background: 'radial-gradient(ellipse at 50% 30%, #1a2540 0%, #090e1d 100%)',
        }}
        onPointerDown={pdfDoc ? onPointerDown : undefined}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {pdfLoading ? (
          <CenteredMsg icon="⏳" title="Loading document…" subtitle="Please wait" muted />
        ) : pdfError ? (
          <CenteredMsg icon="⚠️" title="Failed to load PDF" subtitle={pdfError} danger fallbackSheets={fallbackSheets} />
        ) : !pdfDoc ? (
          <CenteredMsg icon="📋" title="No evaluation document" subtitle="No answer sheets uploaded yet." fallbackSheets={fallbackSheets} />
        ) : (
          /* ── 3D Single Page Stage ── */
          <div className="bv-stage">
            <div
              className={`bv-book-container ${dragAngle !== null && readOnly ? 'bv-dragging' : ''}`}
              style={{
                width: pageSize.width,
                height: pageSize.height,
                position: 'relative',
                transformStyle: 'preserve-3d',
                transform: `scale(${scale})`,
              }}
            >
              {/* Spiral binding rings along left edge */}
              <div className="bv-spiral-binding">
                {Array.from({ length: 14 }).map((_, idx) => (
                  <div key={idx} className="bv-spiral-ring" />
                ))}
              </div>

              {/* Crease shadow */}
              <div className="bv-spine-crease-left" />

              {/* Underlying Base Page (displays target page or blank based on turn threshold) */}
              <div className="bv-page-stack">
                <canvas ref={canvasUnderneathRef} style={{ display: 'block', borderRadius: 4, width: '100%', height: '100%' }} />
              </div>

              {/* Real-time drawing canvas overlay (only active when not turning pages) */}
              {!show3D && !readOnly && (
                <canvas
                  ref={drawCanvasRef}
                  width={pageSize.width}
                  height={pageSize.height}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    cursor: 'crosshair',
                    touchAction: 'none',
                    zIndex: 10,
                  }}
                />
              )}

              {/* 3D Flipping page leaf */}
              {show3D && (
                <div
                  className="bv-leaf-3d"
                  style={{
                    transform: `rotateY(${currentAngle}deg)`,
                    transition: dragAngle !== null ? 'none' : 'transform 0.48s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  }}
                >
                  {/* Front Face: current page content */}
                  <div className="bv-leaf-face bv-leaf-front">
                    <canvas ref={canvasFlipFrontRef} style={{ display: 'block', borderRadius: 4, width: '100%', height: '100%' }} />
                    {/* Shadow overlay during turning */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: `linear-gradient(to right, rgba(0,0,0,${Math.min(0.25, Math.abs(currentAngle) / 180)}) 0%, transparent 100%)`,
                      pointerEvents: 'none',
                    }} />
                  </div>

                  {/* Back Face: blank for next turn, or prev page content for prev turn */}
                  <div className="bv-leaf-face bv-leaf-back">
                    <canvas ref={canvasFlipBackRef} style={{ display: 'block', borderRadius: 4, width: '100%', height: '100%' }} />
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: `linear-gradient(to left, rgba(0,0,0,${Math.min(0.25, (180 - Math.abs(currentAngle)) / 180)}) 0%, transparent 100%)`,
                      pointerEvents: 'none',
                    }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Thumbnail strip ─────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{
          height: 50, background: '#0c1222',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 5, padding: '0 16px', overflow: 'hidden', flexShrink: 0,
        }}>
          {(() => {
            const WINDOW = 25;
            const half = Math.floor(WINDOW / 2);
            let start = Math.max(1, pageNum - half);
            let end   = Math.min(totalPages, start + WINDOW - 1);
            if (end - start < WINDOW - 1) start = Math.max(1, end - WINDOW + 1);
            return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(n => (
              <button
                key={n}
                className="bv-thumb"
                onClick={() => goToPage(n, n > pageNum ? DIR_NEXT : DIR_PREV)}
                style={{
                  width: n === pageNum ? 26 : 19,
                  height: n === pageNum ? 26 : 19,
                  borderRadius: '50%',
                  border: n === pageNum ? '2px solid #319ab9' : '1px solid #1e293b',
                  background: n === pageNum
                    ? 'linear-gradient(135deg, #2563eb, #319ab9)'
                    : '#111827',
                  color: n === pageNum ? '#fff' : '#475569',
                  fontSize: 8, fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.18s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, outline: 'none',
                }}
              >{n}</button>
            ));
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Centered Msg ────────────────────────────────────────────────────────────
function CenteredMsg({ icon, title, subtitle, muted, danger, fallbackSheets = [] }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '60px 40px', textAlign: 'center',
      minHeight: 320,
    }}>
      <div style={{ fontSize: 42, marginBottom: 14 }}>{icon}</div>
      <div style={{
        fontWeight: 700, fontSize: 15, marginBottom: 6,
        color: danger ? '#f87171' : muted ? '#94a3b8' : '#e2e8f0',
      }}>{title}</div>
      <div style={{ fontSize: 12, color: '#475569', maxWidth: 320 }}>{subtitle}</div>
      {fallbackSheets.length > 0 && (
        <div style={{ marginTop: 20, textAlign: 'left', width: '100%', maxWidth: 320 }}>
          <div style={{ color: '#64748b', fontSize: 11, marginBottom: 8, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Individual Sheets
          </div>
          {fallbackSheets.map(s => (
            <a
              key={s.id}
              href={s.file_url || s.file}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                color: '#60a5fa', fontSize: 12, textDecoration: 'none',
                marginBottom: 5, padding: '4px 0',
              }}
            >
              📎 {s.file_name || `Sheet ${s.id}`}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Style helpers ────────────────────────────────────────────────────────────
const navBtnStyle = (disabled) => ({
  padding: '5px 13px',
  borderRadius: 7,
  border: '1px solid #1e293b',
  background: disabled ? '#080d1a' : '#1e293b',
  color: disabled ? '#1e293b' : '#cbd5e1',
  fontSize: 12, fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
  opacity: disabled ? 0.5 : 1,
});

const toolBtnStyle = {
  width: 28, height: 28,
  borderRadius: 7,
  border: '1px solid #334155',
  background: '#111827',
  color: '#94a3b8',
  fontSize: 13, fontWeight: 700,
  cursor: 'pointer', flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'all 0.15s',
  outline: 'none',
};